import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { addCertification, updateCertification, deleteCertification } from '../../../store/slices/certificationsSlice';
import Icon from '../../../components/AppIcon';
import CertificationForm from './CertificationForm';

const CertificationsAdmin = () => {
  const dispatch = useDispatch();
  const { certifications } = useSelector(state => state.certifications);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCertification, setEditingCertification] = useState(null);
  const [expandedSkills, setExpandedSkills] = useState({});

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this certification?')) {
      dispatch(deleteCertification(id));
    }
  };

  const handleAdd = () => {
    setEditingCertification(null);
    setIsFormOpen(true);
  };

  const handleEdit = (certification) => {
    setEditingCertification(certification);
    setIsFormOpen(true);
  };

  const handleSave = (certificationData) => {
    if (editingCertification) {
      dispatch(updateCertification(certificationData));
    } else {
      dispatch(addCertification(certificationData));
    }
    setIsFormOpen(false);
    setEditingCertification(null);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingCertification(null);
  };

  const toggleSkills = (certId) => {
    setExpandedSkills(prev => ({
      ...prev,
      [certId]: !prev[certId]
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'bg-green-100 text-green-800 border-green-200';
      case 'In Progress': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Completed': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'Expired': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const activeCount = certifications.filter(cert => cert.status === 'Active').length;
  const totalCount = certifications.length;

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-primary/5 to-secondary/5 rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-2">Certifications Management</h2>
            <div className="flex items-center space-x-4 text-sm text-text-secondary">
              <div className="flex items-center">
                <Icon name="Award" size={16} className="mr-1 text-primary" />
                <span>{totalCount} total</span>
              </div>
              <div className="flex items-center">
                <Icon name="CheckCircle" size={16} className="mr-1 text-green-600" />
                <span>{activeCount} active</span>
              </div>
              <div className="flex items-center">
                <Icon name="TrendingUp" size={16} className="mr-1 text-blue-600" />
                <span>{Math.round((activeCount / totalCount) * 100)}% active rate</span>
              </div>
            </div>
          </div>
          <button 
            onClick={handleAdd}
            className="bg-primary text-primary-foreground px-6 py-3 rounded-lg hover:bg-primary/90 transition-all duration-200 font-medium flex items-center shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
          >
            <Icon name="Plus" size={18} className="mr-2" />
            Add New Certification
          </button>
        </div>
      </div>

      {/* Certifications Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {certifications.map((cert) => (
          <div key={cert.id} className="bg-surface rounded-xl p-6 security-shadow hover:security-shadow-strong transition-all duration-300 transform hover:-translate-y-1 border border-border/50">
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="relative">
                <div className="w-14 h-14 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl flex items-center justify-center p-2 group-hover:scale-105 transition-transform">
                  {cert.badgeUrl ? (
                    <img 
                      src={cert.badgeUrl} 
                      alt={`${cert.name} badge`}
                      className="w-full h-full object-contain rounded-lg"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <Icon name={cert.icon} size={28} className="text-primary" style={{display: cert.badgeUrl ? 'none' : 'flex'}} />
                </div>
                <div className={`absolute -top-1 -right-1 w-4 h-4 rounded-full ${cert.status === 'Active' ? 'bg-green-500' : 'bg-gray-400'}`} />
              </div>
              <div className="flex space-x-1">
                <button 
                  onClick={() => handleEdit(cert)}
                  className="p-2 text-text-secondary hover:text-primary hover:bg-primary/10 rounded-lg transition-all duration-200"
                  title="Edit certification"
                >
                  <Icon name="Edit" size={16} />
                </button>
                <button 
                  onClick={() => handleDelete(cert.id)}
                  className="p-2 text-text-secondary hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200"
                  title="Delete certification"
                >
                  <Icon name="Trash" size={16} />
                </button>
              </div>
            </div>
            
            {/* Certification Info */}
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-foreground mb-2 line-clamp-2 leading-tight">
                {cert.name}
              </h3>
              <div className="flex items-center justify-between mb-2">
                <p className="text-primary font-medium">{cert.issuer}</p>
                <p className="text-sm text-text-secondary">{cert.date}</p>
              </div>
              
              <div className="flex items-center justify-between mb-3">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(cert.status)}`}>
                  <div className={`w-2 h-2 rounded-full mr-2 ${cert.status === 'Active' ? 'bg-green-500' : 'bg-gray-400'}`} />
                  {cert.status}
                </span>
                {cert.verificationUrl && (
                  <button
                    onClick={() => window.open(cert.verificationUrl, '_blank')}
                    className="text-xs text-primary hover:text-primary/80 font-medium flex items-center hover:underline transition-colors"
                  >
                    <Icon name="ExternalLink" size={12} className="mr-1" />
                    Verify
                  </button>
                )}
              </div>
            </div>

            {/* Credential ID */}
            {cert.credentialId && (
              <div className="mb-4">
                <div className="bg-muted/30 rounded-lg p-3 border border-border/30">
                  <p className="text-xs text-text-secondary mb-1 font-medium">Credential ID</p>
                  <p className="text-xs font-mono text-foreground bg-white/50 px-2 py-1 rounded border">
                    {cert.credentialId}
                  </p>
                </div>
              </div>
            )}

            {/* Skills Section */}
            {cert.skills && cert.skills.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-text-secondary font-medium">Skills ({cert.skills.length})</p>
                  {cert.skills.length > 3 && (
                    <button
                      onClick={() => toggleSkills(cert.id)}
                      className="text-xs text-primary hover:text-primary/80 font-medium flex items-center transition-colors"
                    >
                      {expandedSkills[cert.id] ? (
                        <>
                          <Icon name="ChevronUp" size={12} className="mr-1" />
                          Show Less
                        </>
                      ) : (
                        <>
                          <Icon name="ChevronDown" size={12} className="mr-1" />
                          Show All
                        </>
                      )}
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-1">
                  {(expandedSkills[cert.id] ? cert.skills : cert.skills.slice(0, 3)).map((skill, index) => (
                    <span key={index} className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-primary/10 text-primary font-medium border border-primary/20">
                      {skill}
                    </span>
                  ))}
                  {!expandedSkills[cert.id] && cert.skills.length > 3 && (
                    <button
                      onClick={() => toggleSkills(cert.id)}
                      className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-text-secondary/10 text-text-secondary hover:bg-text-secondary/20 transition-colors border border-text-secondary/20"
                    >
                      +{cert.skills.length - 3} more
                    </button>
                  )}
                </div>
              </div>
            )}
            
            {/* Description */}
            <div className="border-t border-border/30 pt-3">
              <p className="text-text-secondary text-sm line-clamp-3 leading-relaxed">
                {cert.description}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {certifications.length === 0 && (
        <div className="text-center py-16">
          <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-6">
            <Icon name="Award" size={48} className="text-primary" />
          </div>
          <h3 className="text-xl font-semibold text-foreground mb-2">No certifications yet</h3>
          <p className="text-text-secondary mb-6 max-w-md mx-auto">
            Start building your professional profile by adding your first certification.
          </p>
          <button
            onClick={handleAdd}
            className="bg-primary text-primary-foreground px-8 py-3 rounded-lg hover:bg-primary/90 transition-all duration-200 font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
          >
            <Icon name="Plus" size={18} className="mr-2 inline" />
            Add Your First Certification
          </button>
        </div>
      )}

      {/* Form Modal */}
      {isFormOpen && (
        <CertificationForm
          certification={editingCertification}
          onSave={handleSave}
          onClose={handleCloseForm}
        />
      )}
    </div>
  );
};

export default CertificationsAdmin; 